/* eslint-disable react-hooks/exhaustive-deps */

// React
import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';

// Third-party libraries
import { debounce } from 'lodash';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';

// UI libraries
import { Box, Button, Flex, Image, Menu, Spinner, Stack, Table, Text } from '@chakra-ui/react';
import { PageChangeDetails } from 'node_modules/@chakra-ui/react/dist/types/components/pagination/namespace';
import { ArrowDown, ArrowUp, DotsThree, Share, Trash, FunnelSimple } from '@phosphor-icons/react';
import { Tooltip } from '@/components/ui/tooltip';
import { toaster } from '@/components/ui/toaster';
import { Checkbox } from '@/components/ui/checkbox';

// Assets
import emptyCandidateImage from '@/assets/image/empty-candidate-cover.svg';

// Components
import {
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot
} from '@/components/ui/pagination';
import { SearchBox } from '@/components/ui/searchbox';

// Types
import { CandidateWithJobContextBasicInfo } from '@/types/Candidate';
import { CandidateStatus, JobCandidateStatus, getJobCandidateStatusLabel } from '@/types/enums';

// Local components
import AddNewCandidate from './AddNewCandidate';
import { fetchAPI } from '@/api/fetch';
import {
  apiDeleteJobCandidate,
  apiGetJobCandidates,
  GetJobCandidatesResponse,
  GetJobCandidatesSortBy,
  SortOrder,
  GetJobCandidatesParams
} from '@/api/services';
import { getCandidateFullName } from '@/utils';

// Constants
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_DELAY = 300;
const AUTO_RELOAD_INTERVAL = 5000;

// Status filter options
const STATUS_FILTER_OPTIONS = [
  {
    value: JobCandidateStatus.ResumeAnalyzed,
    label: getJobCandidateStatusLabel(JobCandidateStatus.ResumeAnalyzed)
  },
  {
    value: JobCandidateStatus.Shortlist,
    label: getJobCandidateStatusLabel(JobCandidateStatus.Shortlist)
  },
  {
    value: JobCandidateStatus.Interview,
    label: getJobCandidateStatusLabel(JobCandidateStatus.Interview)
  },
  {
    value: JobCandidateStatus.InterviewCompleted,
    label: getJobCandidateStatusLabel(JobCandidateStatus.InterviewCompleted)
  },
  {
    value: JobCandidateStatus.Rejected,
    label: getJobCandidateStatusLabel(JobCandidateStatus.Rejected)
  },
  {
    value: JobCandidateStatus.Pass,
    label: getJobCandidateStatusLabel(JobCandidateStatus.Pass)
  },
  {
    value: JobCandidateStatus.Fatal,
    label: getJobCandidateStatusLabel(JobCandidateStatus.Fatal)
  },
  {
    value: JobCandidateStatus.Withdrawn,
    label: getJobCandidateStatusLabel(JobCandidateStatus.Withdrawn)
  }
];

// Types
interface CandidateListProps {
  jobId: string;
  archived?: boolean;
  onCandidatesChange?: (candidateIds: string[]) => void;
  onRefresh?: (refreshFn: () => void) => void;
}

interface CandidateRowProps {
  candidate: CandidateWithJobContextBasicInfo;
  jobId: string;
  onDelete: (candidateId: string) => void;
  isDeleting: boolean;
}

interface TableHeadersProps {
  sortBy: GetJobCandidatesSortBy;
  sortOrder: SortOrder;
  onSort: (field: GetJobCandidatesSortBy) => void;
  statusFilter: JobCandidateStatus[];
  onStatusFilterChange: (statuses: JobCandidateStatus[]) => void;
}

interface EmptyStateProps {
  jobId: string;
  onAddCandidate: () => void;
  archived: boolean;
}

interface CandidateListState {
  isLoading: boolean;
  autoReload: boolean;
  candidates: CandidateWithJobContextBasicInfo[];
  keyword: string;
  statusFilter: JobCandidateStatus[];
  sortBy: GetJobCandidatesSortBy;
  sortOrder: SortOrder;
  currentPage: number;
  totalPages: number;
  deletingCandidateId: string | null;
}

// Helper functions
const isCandidateDisabled = (status: JobCandidateStatus): boolean => {
  return status === JobCandidateStatus.New;
};

const isCandidateFatal = (
  status: JobCandidateStatus,
  candidateStatus: CandidateStatus
): boolean => {
  return status === JobCandidateStatus.Fatal || candidateStatus === CandidateStatus.Fatal;
};

const getCandidateDisplayName = (candidate: CandidateWithJobContextBasicInfo): string => {
  if (isCandidateFatal(candidate.status, candidate.candidate_status)) {
    return getCandidateFullName(candidate.first_name, candidate.last_name) || 'N/A';
  }

  return getCandidateFullName(candidate.first_name, candidate.last_name) || 'Extracting...';
};

const getCandidateDisplayEmail = (candidate: CandidateWithJobContextBasicInfo): string => {
  if (isCandidateFatal(candidate.status, candidate.candidate_status)) {
    return 'N/A';
  }
  return candidate.email || 'Extracting...';
};

const getCandidateDisplayScore = (candidate: CandidateWithJobContextBasicInfo): React.ReactNode => {
  if (isCandidateFatal(candidate.status, candidate.candidate_status)) {
    return 'N/A';
  }
  if (isCandidateDisabled(candidate.status)) {
    return <Spinner size="sm" color="neutrals.300" />;
  }
  return candidate.overall_score;
};

const getCandidateDisplayStatus = (candidate: CandidateWithJobContextBasicInfo): string => {
  if (isCandidateFatal(candidate.status, candidate.candidate_status)) {
    return 'Fatal';
  }
  if (isCandidateDisabled(candidate.status)) {
    return 'In Progress';
  }
  return getJobCandidateStatusLabel(candidate.status);
};

// Memoized Candidate Row Component
const CandidateRow = memo(({ candidate, jobId, onDelete, isDeleting }: CandidateRowProps) => {
  const navigate = useNavigate();
  const disabled = isCandidateDisabled(candidate.status);
  const isFatal = isCandidateFatal(candidate.status, candidate.candidate_status);

  const handleRowClick = useCallback(() => {
    if (!disabled) {
      navigate(`/job/details/${jobId}/candidate/${candidate.candidate_id}`);
    }
  }, [disabled, navigate, jobId, candidate.candidate_id]);

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(candidate.candidate_id);
    },
    [onDelete, candidate.candidate_id]
  );

  const handleMenuClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  if (isFatal) {
    return (
      <Tooltip
        content={`Failed to extract data from resume "${candidate.original_resume_file_name}". Please verify the file contains valid candidate information and work experience.`}
        openDelay={0}
        closeDelay={0}
        contentProps={{
          css: {
            fontSize: '14px'
          }
        }}
        portalled
      >
        <Table.Row h="56px" _hover={{ bg: 'error.100' }} cursor="disabled" color="error.500">
          <Table.Cell fontSize="14px" width="25%">
            <Box pl="32px">{getCandidateDisplayName(candidate)}</Box>
          </Table.Cell>
          <Table.Cell fontSize="14px" width="15%">
            <Box>{getCandidateDisplayScore(candidate)}</Box>
          </Table.Cell>
          <Table.Cell fontSize="14px" width="20%">
            <Box>{getCandidateDisplayStatus(candidate)}</Box>
          </Table.Cell>
          <Table.Cell fontSize="14px" width="25%">
            <Box>{getCandidateDisplayEmail(candidate)}</Box>
          </Table.Cell>
          <Table.Cell
            fontSize="14px"
            onClick={handleMenuClick}
            textAlign="right"
            pr="32px"
            width="15%"
          >
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button
                  variant="ghost"
                  h="32px"
                  w="32px"
                  p="0"
                  borderRadius="full"
                  _hover={{ bg: 'neutrals.100' }}
                  disabled={isDeleting}
                  aria-label="Candidate actions"
                >
                  {isDeleting ? (
                    <Spinner size="sm" color="neutrals.500" />
                  ) : (
                    <DotsThree weight="bold" color="neutrals.500" />
                  )}
                </Button>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item
                    value="delete"
                    onClick={handleDeleteClick}
                    _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                    fontSize="14px"
                    color="error.500"
                    disabled={isDeleting}
                  >
                    <Flex align="center" gap="8px">
                      {isDeleting ? <Spinner size="sm" color="error.500" /> : <Trash size={16} />}
                      Delete
                    </Flex>
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          </Table.Cell>
        </Table.Row>
      </Tooltip>
    );
  }

  return (
    <Table.Row
      h="56px"
      _hover={{ bg: 'neutrals.50' }}
      onClick={handleRowClick}
      color={disabled ? 'neutrals.500' : 'neutrals.900'}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowClick();
        }
      }}
    >
      <Table.Cell fontSize="14px" width="25%">
        <Box pl="32px">{getCandidateDisplayName(candidate)}</Box>
      </Table.Cell>
      <Table.Cell fontSize="14px" width="15%">
        <Box>{getCandidateDisplayScore(candidate)}</Box>
      </Table.Cell>
      <Table.Cell fontSize="14px" width="20%">
        <Box>{getCandidateDisplayStatus(candidate)}</Box>
      </Table.Cell>
      <Table.Cell fontSize="14px" width="25%">
        <Box>{getCandidateDisplayEmail(candidate)}</Box>
      </Table.Cell>
      <Table.Cell fontSize="14px" onClick={handleMenuClick} textAlign="right" pr="32px" width="15%">
        {candidate.allow_delete && (
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                variant="ghost"
                h="32px"
                w="32px"
                p="0"
                borderRadius="full"
                _hover={{ bg: 'neutrals.100' }}
                disabled={isDeleting}
                aria-label="Candidate actions"
              >
                {isDeleting ? (
                  <Spinner size="sm" color="neutrals.500" />
                ) : (
                  <DotsThree weight="bold" color="neutrals.500" />
                )}
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content>
                <Menu.Item
                  value="delete"
                  onClick={handleDeleteClick}
                  _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                  fontSize="14px"
                  color="error.500"
                  disabled={isDeleting}
                >
                  <Flex align="center" gap="8px">
                    {isDeleting ? <Spinner size="sm" color="error.500" /> : <Trash size={16} />}
                    Delete
                  </Flex>
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        )}
      </Table.Cell>
    </Table.Row>
  );
});

CandidateRow.displayName = 'CandidateRow';

// Memoized Empty State Component
const EmptyState = memo(({ jobId, onAddCandidate, archived }: EmptyStateProps) => (
  <Flex direction="column" align="center" justify="center" mt="64px">
    <Image src={emptyCandidateImage} h="280px" w="280px" alt="No candidates found" />
    <Text fontSize="20px" fontWeight="bold" color="neutrals.700" mt="16px">
      No Candidates Found
    </Text>
    <Text fontSize="16px" color="neutrals.500" mt="8px">
      Import resumes to begin reviewing applicants for this position.
    </Text>
    <Box h="16px" />
    <AddNewCandidate filled jobId={jobId} onAddCandidate={onAddCandidate} disabled={archived} />
  </Flex>
));

EmptyState.displayName = 'EmptyState';

// Memoized Table Headers Component
const TableHeaders = memo(
  ({ sortBy, sortOrder, onSort, statusFilter, onStatusFilterChange }: TableHeadersProps) => {
    const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
    const [tempStatusFilter, setTempStatusFilter] = useState<JobCandidateStatus[]>(statusFilter);

    // Update temp filter when statusFilter prop changes
    useEffect(() => {
      setTempStatusFilter(statusFilter);
    }, [statusFilter]);

    // Initialize temp filter with all statuses when filter is opened
    const handleFilterOpen = useCallback(() => {
      if (statusFilter.length === 0) {
        setTempStatusFilter(STATUS_FILTER_OPTIONS.map((option) => option.value));
      } else {
        setTempStatusFilter(statusFilter);
      }
      setIsStatusFilterOpen(true);
    }, [statusFilter]);

    const headers = [
      { key: 'name' as GetJobCandidatesSortBy, label: 'Name', width: '25%' },
      { key: 'score' as GetJobCandidatesSortBy, label: 'Score', width: '15%' },
      { key: null, label: 'Status', width: '20%', hasFilter: true },
      { key: null, label: 'Email', width: '25%' },
      { key: null, label: '', width: '15%' }
    ];

    const getSortIcon = (field: GetJobCandidatesSortBy | null) => {
      if (field === null || sortBy !== field) return null;
      return sortOrder === 'asc' ? (
        <ArrowUp size={16} weight="bold" />
      ) : (
        <ArrowDown size={16} weight="bold" />
      );
    };

    const handleSortClick = useCallback(
      (field: GetJobCandidatesSortBy | null) => {
        if (field !== null) {
          onSort(field);
        }
      },
      [onSort]
    );

    const handleStatusToggle = useCallback((status: JobCandidateStatus) => {
      setTempStatusFilter((prev) => {
        const newFilter = prev.includes(status)
          ? prev.filter((s) => s !== status)
          : [...prev, status];
        return newFilter;
      });
    }, []);

    const handleSelectAll = useCallback(() => {
      setTempStatusFilter((prev) => {
        if (prev.length === STATUS_FILTER_OPTIONS.length) {
          return [];
        } else {
          return STATUS_FILTER_OPTIONS.map((option) => option.value);
        }
      });
    }, []);

    const handleApply = useCallback(() => {
      if (tempStatusFilter.length > 0) {
        onStatusFilterChange(tempStatusFilter);
        setIsStatusFilterOpen(false);
      }
    }, [tempStatusFilter, onStatusFilterChange]);

    const handleCancel = useCallback(() => {
      setTempStatusFilter(statusFilter);
      setIsStatusFilterOpen(false);
    }, [statusFilter]);

    const isApplyDisabled = tempStatusFilter.length === 0;

    return (
      <Table.Row bg="neutrals.50" h="48px">
        {headers.map((header, index) => (
          <Table.ColumnHeader
            key={header.key || `empty-${index}`}
            fontSize="14px"
            fontWeight="bold"
            borderColor="neutrals.300"
            pl={index === 0 ? '32px' : '8px'}
            textAlign={header.label === '' ? 'right' : 'left'}
            pr={header.label === '' ? '32px' : 0}
            cursor={header.key === null ? 'default' : 'pointer'}
            onClick={() => handleSortClick(header.key)}
            _hover={header.key !== null ? { bg: 'neutrals.100' } : {}}
            width={header.width}
            role={header.key !== null ? 'button' : undefined}
            tabIndex={header.key !== null ? 0 : undefined}
            onKeyDown={(e) => {
              if (header.key !== null && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleSortClick(header.key);
              }
            }}
          >
            <Flex align="center" gap="8px">
              {header.label}
              {getSortIcon(header.key) && (
                <Text fontSize="12px" color="primary.900">
                  {getSortIcon(header.key)}
                </Text>
              )}
              {header.hasFilter && (
                <Menu.Root
                  open={isStatusFilterOpen}
                  onOpenChange={(details) => {
                    if (details.open) {
                      handleFilterOpen();
                    } else {
                      setIsStatusFilterOpen(false);
                    }
                  }}
                >
                  <Menu.Trigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      h="24px"
                      w="24px"
                      p="0"
                      borderRadius="full"
                      bg={
                        statusFilter.length !== 0 &&
                        statusFilter.length !== STATUS_FILTER_OPTIONS.length
                          ? 'primary.100'
                          : 'transparent'
                      }
                      _hover={{ bg: 'neutrals.100' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterOpen();
                      }}
                      aria-label="Filter by status"
                    >
                      <FunnelSimple size={12} weight="bold" />
                    </Button>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content minW="240px" p="16px">
                      <Flex direction="column" gap="12px">
                        <Text fontSize="14px" fontWeight="bold" color="neutrals.700">
                          Filter by Status
                        </Text>
                        <Checkbox
                          size="sm"
                          checked={tempStatusFilter.length === STATUS_FILTER_OPTIONS.length}
                          onCheckedChange={handleSelectAll}
                          cursor="pointer"
                        >
                          Select All
                        </Checkbox>
                        <Box borderTop="1px solid" borderColor="neutrals.200" my="4px" />
                        {STATUS_FILTER_OPTIONS.map((option) => (
                          <Checkbox
                            key={option.value}
                            size="sm"
                            checked={tempStatusFilter.includes(option.value)}
                            onCheckedChange={() => handleStatusToggle(option.value)}
                            cursor="pointer"
                          >
                            {option.label}
                          </Checkbox>
                        ))}
                        <Box borderTop="1px solid" borderColor="neutrals.200" my="8px" />
                        <Flex gap="8px" justify="flex-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            borderRadius="8px"
                            onClick={handleCancel}
                            fontSize="14px"
                            h="32px"
                            px="12px"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleApply}
                            disabled={isApplyDisabled}
                            borderRadius="8px"
                            fontSize="14px"
                            h="32px"
                            px="12px"
                            bg={isApplyDisabled ? 'neutrals.200' : 'primary.900'}
                            color={isApplyDisabled ? 'neutrals.500' : 'white'}
                            _hover={isApplyDisabled ? {} : { bg: 'primary.800' }}
                            cursor={isApplyDisabled ? 'not-allowed' : 'pointer'}
                          >
                            Apply
                          </Button>
                        </Flex>
                        {isApplyDisabled && (
                          <Text
                            fontSize="12px"
                            fontWeight="normal"
                            color="error.500"
                            textAlign="right"
                          >
                            Please select at least one status
                          </Text>
                        )}
                      </Flex>
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              )}
            </Flex>
          </Table.ColumnHeader>
        ))}
      </Table.Row>
    );
  }
);

TableHeaders.displayName = 'TableHeaders';

// Loading Row Component
const LoadingRow = memo(() => (
  <Table.Row h="56px">
    <Table.Cell colSpan={5} textAlign="center">
      <Spinner size="lg" borderWidth="4px" color="primary.900" my="32px" />
    </Table.Cell>
  </Table.Row>
));

LoadingRow.displayName = 'LoadingRow';

// Main Component
export default function CandidateList({
  jobId,
  archived = false,
  onCandidatesChange,
  onRefresh
}: CandidateListProps) {
  const [state, setState] = useState<CandidateListState>({
    isLoading: false,
    autoReload: false,
    candidates: [],
    keyword: '',
    statusFilter: [],
    sortBy: 'created_at',
    sortOrder: 'desc',
    currentPage: 1,
    totalPages: 0,
    deletingCandidateId: null
  });

  const exportData = useMemo(
    () =>
      state.candidates.map(({ first_name, last_name, email, overall_score, status }) => ({
        FirstName: first_name,
        LastName: last_name,
        Email: email,
        Score: overall_score,
        Status: getJobCandidateStatusLabel(status)
      })),
    [state.candidates]
  );

  const fetchCandidates = useCallback(
    async (
      showLoading: boolean,
      page: number,
      limit: number,
      sort_by: GetJobCandidatesSortBy,
      sort_order: SortOrder,
      keyword: string,
      status_filter: JobCandidateStatus[]
    ) => {
      if (showLoading) {
        setState((prev) => ({ ...prev, isLoading: true }));
      }

      try {
        const params: GetJobCandidatesParams = {
          page,
          limit,
          sort_by,
          sort_order,
          keyword
        };
        if (status_filter.length > 0) {
          params.status = status_filter;
        }

        const response = await fetchAPI<GetJobCandidatesResponse>({
          request: () =>
            apiGetJobCandidates({
              jobId,
              params
            })
        });

        if (response.success) {
          const { candidates, total_pages, current_page } = response.data;
          const hasNewCandidates = candidates.some(
            (candidate) => candidate.status === JobCandidateStatus.New
          );

          setState((prev) => ({
            ...prev,
            candidates,
            totalPages: total_pages,
            currentPage: current_page,
            autoReload: hasNewCandidates,
            isLoading: false
          }));

          // Call the callback with candidate IDs
          onCandidatesChange?.(candidates.map((c) => c.candidate_id));
        } else {
          console.error('Failed to fetch candidates:', response.message);
          toaster.create({
            title: 'Failed to fetch candidates',
            description: response.message,
            type: 'error'
          });
        }
      } catch (error) {
        console.error('Error fetching candidates:', error);
        toaster.create({
          title: 'Error fetching candidates',
          description: 'Please try again later',
          type: 'error'
        });
      } finally {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [jobId]
  );

  useEffect(() => {
    fetchCandidates(
      true,
      state.currentPage,
      PAGE_SIZE,
      state.sortBy,
      state.sortOrder,
      state.keyword,
      state.statusFilter
    );
  }, [
    state.currentPage,
    state.sortBy,
    state.sortOrder,
    state.keyword,
    state.statusFilter,
    fetchCandidates
  ]);

  useEffect(() => {
    if (!state.autoReload) return;

    const intervalId = setInterval(
      () =>
        fetchCandidates(
          false,
          state.currentPage,
          PAGE_SIZE,
          state.sortBy,
          state.sortOrder,
          state.keyword,
          state.statusFilter
        ),
      AUTO_RELOAD_INTERVAL
    );

    return () => clearInterval(intervalId);
  }, [
    state.autoReload,
    state.currentPage,
    state.sortBy,
    state.sortOrder,
    state.keyword,
    state.statusFilter,
    fetchCandidates
  ]);

  // Event handlers
  const handlePageChange = useCallback((details: PageChangeDetails) => {
    setState((prev) => ({ ...prev, currentPage: details.page }));
  }, []);

  const searchCandidate = useCallback(
    debounce((keyword: string) => {
      const normalizedKeyword = keyword.toLowerCase();
      setState((prev) => ({
        ...prev,
        keyword: normalizedKeyword,
        currentPage: 1
      }));
    }, SEARCH_DEBOUNCE_DELAY),
    []
  );

  const handleStatusFilterChange = useCallback((statuses: JobCandidateStatus[]) => {
    setState((prev) => ({
      ...prev,
      statusFilter: statuses,
      currentPage: 1,
      sortBy: 'created_at',
      sortOrder: 'desc'
    }));
  }, []);

  const handleDeleteCandidate = useCallback(
    async (candidateId: string) => {
      setState((prev) => ({ ...prev, deletingCandidateId: candidateId }));

      try {
        const response = await fetchAPI({
          request: () => apiDeleteJobCandidate({ jobId, candidateId })
        });

        if (response.success) {
          fetchCandidates(
            false,
            state.currentPage,
            PAGE_SIZE,
            state.sortBy,
            state.sortOrder,
            state.keyword,
            state.statusFilter
          );
          toaster.create({
            title: 'Candidate deleted successfully',
            type: 'success'
          });
        } else {
          console.error('Failed to delete candidate:', response.message);
          toaster.create({
            title: 'Failed to delete candidate',
            description: response.message || 'Please try again.',
            type: 'error'
          });
        }
      } catch (error) {
        console.error('Error deleting candidate:', error);
        toaster.create({
          title: 'Failed to delete candidate',
          description: 'Please try again.',
          type: 'error'
        });
      } finally {
        setState((prev) => ({ ...prev, deletingCandidateId: null }));
      }
    },
    [
      jobId,
      state.currentPage,
      state.sortBy,
      state.sortOrder,
      state.keyword,
      state.statusFilter,
      fetchCandidates
    ]
  );

  const handleSort = useCallback((field: GetJobCandidatesSortBy) => {
    // If clicking a different field, set it as sort by
    // Name field defaults to asc, others default to desc
    const defaultOrder = field === 'name' ? 'asc' : 'desc';

    setState((prev) => {
      if (prev.sortBy === field) {
        // If clicking the same field, toggle sort order
        if (prev.sortOrder !== defaultOrder) {
          return {
            ...prev,
            sortBy: 'created_at',
            sortOrder: 'desc'
          };
        } else {
          return {
            ...prev,
            sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc'
          };
        }
      } else {
        return {
          ...prev,
          sortBy: field,
          sortOrder: defaultOrder
        };
      }
    });
  }, []);

  const handleAddCandidate = useCallback(() => {
    fetchCandidates(
      false,
      state.currentPage,
      PAGE_SIZE,
      state.sortBy,
      state.sortOrder,
      state.keyword,
      state.statusFilter
    );
  }, [
    fetchCandidates,
    state.currentPage,
    state.sortBy,
    state.sortOrder,
    state.keyword,
    state.statusFilter
  ]);

  const refreshCandidates = useCallback(() => {
    fetchCandidates(
      false,
      state.currentPage,
      PAGE_SIZE,
      state.sortBy,
      state.sortOrder,
      state.keyword,
      state.statusFilter
    );
  }, [
    fetchCandidates,
    state.currentPage,
    state.sortBy,
    state.sortOrder,
    state.keyword,
    state.statusFilter
  ]);

  // Pass the refresh function to parent component
  useEffect(() => {
    onRefresh?.(refreshCandidates);
  }, [onRefresh, refreshCandidates]);

  // Render
  return (
    <Flex direction="column" width="100%" marginTop="40px">
      {/* Header with search and actions */}
      <Flex justifyContent="space-between" alignItems="center" marginBottom="24px">
        <SearchBox
          id="searchCandidate"
          placeholder="Search candidate name"
          search={searchCandidate}
        />
        <Flex gap="16px">
          <AddNewCandidate jobId={jobId} onAddCandidate={handleAddCandidate} disabled={archived} />
          <CSVLink data={exportData} filename="candidates.csv">
            <Button
              borderColor="primary.900"
              bg="background.secondary"
              color="primary.900"
              borderRadius="8px"
              fontSize="16px"
              aria-label="Export candidates to CSV"
              _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
            >
              <Share size={24} />
              Export
            </Button>
          </CSVLink>
        </Flex>
      </Flex>

      {/* Table */}
      <Table.Root>
        <Table.Header>
          <TableHeaders
            sortBy={state.sortBy}
            sortOrder={state.sortOrder}
            onSort={handleSort}
            statusFilter={state.statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
          />
        </Table.Header>
        <Table.Body>
          {state.isLoading ? (
            <LoadingRow />
          ) : (
            state.candidates.map((candidate) => (
              <CandidateRow
                key={candidate.candidate_id}
                candidate={candidate}
                jobId={jobId}
                onDelete={handleDeleteCandidate}
                isDeleting={state.deletingCandidateId === candidate.candidate_id}
              />
            ))
          )}
        </Table.Body>
      </Table.Root>

      {/* Pagination or Empty State */}
      {state.candidates.length > 0 ? (
        <Flex justify="flex-end" align="center" mt="4">
          <PaginationRoot
            count={state.totalPages * PAGE_SIZE}
            pageSize={PAGE_SIZE}
            page={state.currentPage}
            onPageChange={handlePageChange}
          >
            <Stack direction="row" gap="8px">
              <PaginationPrevTrigger />
              <Text fontSize="14px" color="neutrals.700" alignSelf="center">
                {state.currentPage} / {state.totalPages}
              </Text>
              <PaginationNextTrigger />
            </Stack>
          </PaginationRoot>
        </Flex>
      ) : (
        !state.isLoading && (
          <EmptyState jobId={jobId} onAddCandidate={handleAddCandidate} archived={archived} />
        )
      )}
    </Flex>
  );
}
